import { Controller } from '@hotwired/stimulus'
import { i18n } from '../lib/i18n'

/**
 * Expression Builder Controller
 * Visual drag-drop interface for building SQL expressions
 */
export default class extends Controller {
  static targets = [
    'output',
    'preview',
    'canvas',
    'functionPalette',
    'columnSelect'
  ]
  static values = {
    tableName: String,
    columns: Array
  }

  connect() {
    this.expression = ''
    this.updatePreview()
  }

  // Insert a function template
  insertFunction(event) {
    const func = event.currentTarget.dataset.function
    const template = this.getFunctionTemplate(func)
    this.insertAtCursor(template)
  }

  // Insert a column reference
  insertColumn(event) {
    const column = event.currentTarget.dataset.column || this.columnSelectTarget.value
    if (!column) return

    const expr = `${this.tableNameValue}.${column}`
    this.insertAtCursor(expr)
  }

  // Insert text at cursor position in output field
  insertAtCursor(text) {
    const output = this.outputTarget
    const start = output.selectionStart
    const end = output.selectionEnd
    const value = output.value

    output.value = value.substring(0, start) + text + value.substring(end)
    output.selectionStart = output.selectionEnd = start + text.length
    output.focus()

    this.expression = output.value
    this.updatePreview()
  }

  // Handle direct input changes
  inputChanged() {
    this.expression = this.outputTarget.value
    this.updatePreview()
  }

  // Update the syntax-highlighted preview
  updatePreview() {
    if (!this.hasPreviewTarget) return

    const expr = this.expression || this.outputTarget.value
    if (!expr) {
      this.previewTarget.innerHTML = `<span class="text-gray-400">${i18n.t('expression_builder.preview_placeholder')}</span>`
      return
    }

    this.previewTarget.innerHTML = this.highlightSql(expr)
  }

  // Get function template with placeholder
  getFunctionTemplate(func) {
    const templates = {
      // Aggregate functions
      'COUNT': 'COUNT(*)',
      'COUNT_DISTINCT': 'COUNT(DISTINCT column)',
      'SUM': 'SUM(column)',
      'AVG': 'AVG(column)',
      'MIN': 'MIN(column)',
      'MAX': 'MAX(column)',

      // String functions
      'CONCAT': "CONCAT(column1, ' ', column2)",
      'UPPER': 'UPPER(column)',
      'LOWER': 'LOWER(column)',
      'TRIM': 'TRIM(column)',
      'SUBSTRING': 'SUBSTRING(column, 1, 10)',
      'LENGTH': 'LENGTH(column)',
      'COALESCE': "COALESCE(column, 'default')",

      // Date functions
      'EXTRACT_YEAR': 'EXTRACT(YEAR FROM column)',
      'EXTRACT_MONTH': 'EXTRACT(MONTH FROM column)',
      'EXTRACT_DAY': 'EXTRACT(DAY FROM column)',
      'EXTRACT_DOW': 'EXTRACT(DOW FROM column)',
      'DATE_TRUNC_DAY': "DATE_TRUNC('day', column)",
      'DATE_TRUNC_WEEK': "DATE_TRUNC('week', column)",
      'DATE_TRUNC_MONTH': "DATE_TRUNC('month', column)",
      'AGE': 'EXTRACT(YEAR FROM AGE(CURRENT_DATE, column))',
      'CURRENT_DATE': 'CURRENT_DATE',
      'NOW': 'NOW()',

      // Numeric functions
      'ROUND': 'ROUND(column, 2)',
      'ABS': 'ABS(column)',
      'CEIL': 'CEIL(column)',
      'FLOOR': 'FLOOR(column)',

      // Conditional
      'CASE': "CASE WHEN column = 'value' THEN 'Yes' ELSE 'No' END",
      'NULLIF': 'NULLIF(column, 0)',
      'GREATEST': 'GREATEST(column1, column2)',
      'LEAST': 'LEAST(column1, column2)'
    }

    return templates[func] || func + '()'
  }

  // Simple SQL syntax highlighting
  highlightSql(sql) {
    const keywords = ['SELECT', 'FROM', 'WHERE', 'AND', 'OR', 'NOT', 'IN', 'IS', 'NULL', 'AS',
      'CASE', 'WHEN', 'THEN', 'ELSE', 'END', 'LIKE', 'ILIKE', 'BETWEEN',
      'TRUE', 'FALSE', 'DISTINCT', 'ALL', 'ANY']

    const functions = ['COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'COALESCE', 'NULLIF',
      'CONCAT', 'UPPER', 'LOWER', 'TRIM', 'SUBSTRING', 'LENGTH',
      'EXTRACT', 'DATE_TRUNC', 'AGE', 'NOW', 'CURRENT_DATE',
      'ROUND', 'ABS', 'CEIL', 'FLOOR', 'GREATEST', 'LEAST']

    let result = this.escapeHtml(sql)

    // Highlight strings
    result = result.replace(/'([^']*)'/g, '<span class="text-green-600">\'$1\'</span>')

    // Highlight numbers
    result = result.replace(/\b(\d+\.?\d*)\b/g, '<span class="text-purple-600">$1</span>')

    // Highlight keywords
    keywords.forEach(kw => {
      const regex = new RegExp(`\\b(${kw})\\b`, 'gi')
      result = result.replace(regex, '<span class="text-blue-600 font-semibold">$1</span>')
    })

    // Highlight functions
    functions.forEach(fn => {
      const regex = new RegExp(`\\b(${fn})\\s*\\(`, 'gi')
      result = result.replace(regex, '<span class="text-orange-600 font-semibold">$1</span>(')
    })

    // Highlight table.column references
    result = result.replace(/(\w+)\.(\w+)/g, '<span class="text-gray-600">$1</span>.<span class="text-cyan-600">$2</span>')

    return result
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // Quick template buttons
  applyTemplate(event) {
    const template = event.currentTarget.dataset.template
    this.outputTarget.value = template
    this.expression = template
    this.updatePreview()
  }

  // Clear the expression
  clear() {
    this.outputTarget.value = ''
    this.expression = ''
    this.updatePreview()
  }
}
